import { t } from "../../i18n";
import type {
  BackendApiEndpoint,
  BackendApiRequestField,
} from "./backend-api-coverage-types";

export function requestFieldsFor(
  endpoint: BackendApiEndpoint,
): BackendApiRequestField[] {
  return uniqueFields([
    ...endpoint.doc.request_fields,
    ...codeFields(endpoint),
  ]);
}

export function combinedErrorCodes(endpoint: BackendApiEndpoint): string[] {
  return [
    ...new Set([...endpoint.code.error_codes, ...endpoint.doc.error_codes]),
  ];
}

export function responseSummaryFor(endpoint: BackendApiEndpoint): string {
  const json = endpoint.code.response_json
    .map((shape) => `JSON ${shape}`)
    .join(", ");
  return (
    json ||
    formatFactValues(endpoint.code.response).join(", ") ||
    endpoint.doc.response_summary ||
    t("api_coverage.doc_value_missing")
  );
}

export function formatFactValues(values: string[]): string[] {
  return values.map((value) => factTranslations[value] ?? value);
}

function codeFields(endpoint: BackendApiEndpoint): BackendApiRequestField[] {
  return [
    ...endpoint.code.headers.map((name) =>
      field(
        name,
        t("api_coverage.field_type.header"),
        name.toLowerCase() === "upgrade" ? t("common.yes") : t("common.no"),
        t("api_coverage.code_field.header"),
      ),
    ),
    ...endpoint.code.path_params.map((name) =>
      field(
        name,
        t("api_coverage.field_type.path"),
        t("common.yes"),
        t("api_coverage.code_field.path"),
      ),
    ),
    ...endpoint.code.query_params.map((name) =>
      field(
        name,
        t("api_coverage.field_type.query"),
        t("common.no"),
        t("api_coverage.code_field.query"),
      ),
    ),
    ...endpoint.code.body.map((name) =>
      field(
        "body",
        t("api_coverage.field_type.body"),
        t("api_coverage.code_read"),
        t("api_coverage.code_field.body", {
          value: formatFactValues([name])[0],
        }),
      ),
    ),
  ];
}

function field(
  name: string,
  type: string,
  required: string,
  description: string,
): BackendApiRequestField {
  return {
    name,
    type,
    required,
    description,
    columns: ["field", "type", "required", "description"],
  };
}

function uniqueFields(
  fields: BackendApiRequestField[],
): BackendApiRequestField[] {
  const byKey = new Map<string, BackendApiRequestField>();
  for (const fieldValue of fields) {
    const key = `${fieldValue.name.toLowerCase()}::${fieldValue.type.toLowerCase()}`;
    if (!byKey.has(key)) byKey.set(key, fieldValue);
  }
  return [...byKey.values()];
}

const factTranslations: Record<string, string> = {
  "Attach expected agent id for DO auth verification": t(
    "api_coverage.logic.attach_expected_agent_id",
  ),
  "Business errors are raised with AppError": t(
    "api_coverage.validation.app_error",
  ),
  "Parse request JSON": t("api_coverage.logic.parse_json"),
  "Path parameters are read": t("api_coverage.validation.path_params"),
  "Proxies upgrade/runtime response": t("api_coverage.response.proxy"),
  "Proxy request to downstream runtime": t(
    "api_coverage.logic.proxy_downstream",
  ),
  "Read path parameters": t("api_coverage.logic.read_path"),
  "Read query parameters": t("api_coverage.logic.read_query"),
  "Query parameters are read": t("api_coverage.validation.query_params"),
  "Reads JSON body": t("api_coverage.fact.reads_json"),
  "Reads URL/query": t("api_coverage.fact.reads_url"),
  "Reads binary body": t("api_coverage.fact.reads_binary"),
  "Reads multipart/form body": t("api_coverage.fact.reads_form"),
  "Reads text body": t("api_coverage.fact.reads_text"),
  "Reads validated JSON body": t("api_coverage.fact.reads_valid_json"),
  "Returns JSON response": t("api_coverage.response.json"),
  "Returns raw body response": t("api_coverage.response.raw"),
  "Returns redirect response": t("api_coverage.response.redirect"),
  "Returns text response": t("api_coverage.response.text"),
  "Return JSON response": t("api_coverage.logic.return_json"),
  "Route to Durable Object": t("api_coverage.logic.route_do"),
  "Schedule async side effect": t("api_coverage.logic.wait_until"),
  "Schema validation is enforced": t("api_coverage.validation.schema"),
  "Upgrade header must equal websocket": t("api_coverage.validation.upgrade"),
  "Validate WebSocket Upgrade header": t("api_coverage.logic.validate_ws"),
  "Validate request schema": t("api_coverage.logic.validate_schema"),
};
